import { useState, useEffect, useRef } from 'react'
import { bannerService } from '../lib/bannerService'
import { logger } from '../lib/logger'
import { isMock } from '../lib/firebase'
import type { Banner } from '../types/banner'
import { ExternalLink } from 'lucide-react'
import OptimizedImage from './OptimizedImage'

interface BannerDisplayProps {
  position?: Banner['position']
  className?: string
  limit?: number
  banners?: Banner[]
}

const BannerDisplay = ({ position, className = '', limit = 4, banners: bannersProp }: BannerDisplayProps) => {
  const [banners, setBanners] = useState<Banner[]>([])
  const [loading, setLoading] = useState(true)
  const countedBannersRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    // If banners are provided via prop, use them directly
    if (bannersProp !== undefined) {
      logger.log('BannerDisplay: using banners from prop:', bannersProp.length)
      setBanners(bannersProp)
      setLoading(false)
      return
    }

    // Otherwise fetch banners based on position
    if (!position) {
      setBanners([])
      setLoading(false)
      return
    }

    const loadBanners = async () => {
      try {
        setLoading(true)
        // Fetch active banners for this position
        const allBanners = await bannerService.getBanners({ position, active: true })
        
        let filtered = allBanners
        
        // Only filter by date in production (in mock mode, show all banners regardless of date)
        if (!isMock) {
          const now = new Date().toISOString().split('T')[0] // YYYY-MM-DD
          logger.log('BannerDisplay date filter: now =', now, 'isMock =', isMock, 'allBanners count =', allBanners.length)
          filtered = allBanners.filter(banner => {
            const startValid = !banner.startDate || banner.startDate <= now
            const endValid = !banner.endDate || banner.endDate >= now
            if (!startValid || !endValid) {
              logger.log('Banner filtered out by date:', banner.title, 'startDate:', banner.startDate, 'endDate:', banner.endDate)
              return false
            }
            return true
          })
          logger.log('BannerDisplay after date filter:', filtered.length)
        }
        
        // Shuffle randomly
        const shuffled = [...filtered]
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
        }
        // Apply limit
        const finalBanners = shuffled.slice(0, limit)
        logger.log('BannerDisplay:', position, 'filtered banners:', filtered.length, 'final:', finalBanners.length, 'limit:', limit, 'mock:', isMock)
        setBanners(finalBanners)
      } catch (error) {
        logger.error('Error loading banners:', error)
      } finally {
        setLoading(false)
      }
    }

    loadBanners()
  }, [position, limit, bannersProp])

  // Record impressions when banners are displayed
  useEffect(() => {
    if (banners.length === 0 || loading) return

    banners.forEach(banner => {
      if (!countedBannersRef.current.has(banner.id)) {
        countedBannersRef.current.add(banner.id)
        bannerService.incrementImpression(banner.id).catch(logger.error)
      }
    })
  }, [banners, loading])

  const handleBannerClick = async (bannerId: string) => {
    try {
      await bannerService.incrementClick(bannerId)
    } catch (error) {
      logger.error('Error recording click:', error)
    }
  }

  if (loading) {
    return (
      <div className={`flex justify-center items-center h-32 ${className}`}>
        <div className="text-noe-500 text-sm">Carregando...</div>
      </div>
    )
  }

   logger.log('BannerDisplay rendering:', banners.length, 'banners for position', position, banners)
   
   const isCompact = position === 'header-top' || position === 'footer-bottom'

  if (banners.length === 0) {
    // Return empty space with minimal height for layout consistency
    return <div className={`${className} min-h-[100px]`}></div>
  }

  // For compact positions (header-top, footer-bottom), display banners horizontally
  if (isCompact) {
    const isFewBanners = banners.length <= 3
    return (
      <div className={`flex flex-row gap-4 ${isFewBanners ? '' : 'overflow-x-auto scrollbar-thin scrollbar-thumb-noe-800 scrollbar-track-transparent pb-2'} ${className}`}>
        {banners.map(banner => (
          <div
            key={banner.id}
            className={`relative overflow-hidden rounded-xl border border-noe-800 bg-noe-900/30 hover:border-noe-brand/30 transition-all group ${isFewBanners ? 'flex-1 min-w-0' : 'flex-shrink-0 min-w-[180px] max-w-[280px]'}`}
          >
            {banner.link ? (
              <a
                href={banner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full"
                onClick={() => handleBannerClick(banner.id)}
              >
                <div className="relative overflow-hidden w-full max-h-[70px]">
                  <OptimizedImage
                    src={banner.imageUrl}
                    alt={banner.altText}
                    className="w-full h-auto object-cover max-h-[70px]"
                    width={800}
                    height={200}
                    priority={true}
                  />
                  <div className="absolute top-1 right-1 p-1 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink className="w-2 h-2 text-white" />
                  </div>
                </div>
                {(banner.title || banner.description) && (
                  <div className="p-2">
                    {banner.title && (
                      <h4 className="font-medium text-noe-100 mb-0.5 break-words text-sm truncate">{banner.title}</h4>
                    )}
                    {banner.description && (
                      <p className="text-xs text-noe-400 break-words line-clamp-2">{banner.description}</p>
                    )}
                  </div>
                )}
              </a>
            ) : (
              <div className="h-full">
                <div className="overflow-hidden w-full max-h-[70px]">
                  <OptimizedImage
                    src={banner.imageUrl}
                    alt={banner.altText}
                    className="w-full h-auto object-cover max-h-[70px]"
                    width={800}
                    height={200}
                    priority={true}
                  />
                </div>
                {(banner.title || banner.description) && (
                  <div className="p-2">
                    {banner.title && (
                      <h4 className="font-medium text-noe-100 mb-0.5 break-words text-sm truncate">{banner.title}</h4>
                    )}
                    {banner.description && (
                      <p className="text-xs text-noe-400 break-words line-clamp-2">{banner.description}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  // Regular vertical layout for sidebar, popup, etc.
  return (
    <div className={`space-y-4 ${className}`}>
      {banners.map(banner => (
        <div
          key={banner.id}
          className="relative overflow-hidden rounded-xl border border-noe-800 bg-noe-900/30 hover:border-noe-brand/30 transition-all group"
        >
          {banner.link ? (
            <a
              href={banner.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              onClick={() => handleBannerClick(banner.id)}
            >
              <div className="relative overflow-hidden max-h-[300px]">
                <OptimizedImage
                  src={banner.imageUrl}
                  alt={banner.altText}
                  className="w-full h-auto object-cover max-h-[300px]"
                  width={800}
                  height={400}
                  priority={true}
                />
                <div className="absolute top-2 right-2 p-1 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <ExternalLink className="w-3 h-3 text-white" />
                </div>
              </div>
              {(banner.title || banner.description) && (
                <div className="p-4">
                  {banner.title && (
                    <h4 className="font-medium text-noe-100 mb-1 break-words">{banner.title}</h4>
                  )}
                  {banner.description && (
                    <p className="text-sm text-noe-400 break-words">{banner.description}</p>
                  )}
                </div>
              )}
            </a>
          ) : (
            <>
              <div className="overflow-hidden max-h-[300px]">
                <OptimizedImage
                  src={banner.imageUrl}
                  alt={banner.altText}
                  className="w-full h-auto object-cover max-h-[300px]"
                  width={800}
                  height={400}
                  priority={true}
                />
              </div>
              {(banner.title || banner.description) && (
                <div className="p-4">
                  {banner.title && (
                    <h4 className="font-medium text-noe-100 mb-1 break-words">{banner.title}</h4>
                  )}
                  {banner.description && (
                    <p className="text-sm text-noe-400 break-words">{banner.description}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  )
}

export default BannerDisplay